import { useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import MfaService from '../../../shared/services/mfa/mfa.service';
import { IMfaStatus } from '../../../shared/services/mfa/mfa.model';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { TotpSetupDialog } from '../../dialogs/settings/profile/totp-setup-dialog';
import { BackupCodesDialog } from '../../dialogs/settings/profile/backup-codes-dialog';

// "Two-factor authentication" block on the personal-settings profile page. Two
// independent factors: an authenticator app (with single-use backup codes) and
// emailed codes. Hidden entirely for SSO users, whose identity provider owns MFA.
export const TwoFactorSettings = () => {
  const mfaService = new MfaService();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [status, setStatus] = useState<IMfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [revealedCodes, setRevealedCodes] = useState<string[] | null>(null);

  const refresh = () => mfaService.getStatus()
    .then(setStatus)
    .catch(() => { /* the http interceptor handles auth errors */ })
    .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toast = (message: string) => userNotificationsContext.openSnackbar({ open: true, message });
  const errorToast = (err?: any) => userNotificationsContext.openSnackbar({
    open: true,
    message: err?.response?.data?.message || 'There was an error, please try again',
    backgroundColor: 'text.primary',
  });

  const confirm = (title: string, message: string, buttonCopy: string) =>
    userNotificationsContext.openDialog({ active: true, title, message, buttonCopy, cancelButtonCopy: 'Cancel' });

  const enableEmail = async () => {
    setBusy(true);
    try {
      await mfaService.enableEmail();
      await refresh();
      toast('Email verification codes turned on');
    } catch (err) {
      errorToast(err);
    } finally {
      setBusy(false);
    }
  };

  const disableEmail = async () => {
    const confirmed = await confirm(
      'Turn off email codes?',
      'You will no longer be asked for an emailed code when you sign in.',
      'Turn off',
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      await mfaService.disableEmail();
      await refresh();
      toast('Email verification codes turned off');
    } catch (err) {
      errorToast(err);
    } finally {
      setBusy(false);
    }
  };

  const disableTotp = async () => {
    const confirmed = await confirm(
      'Remove authenticator app?',
      'You will no longer be asked for a code from your app, and your backup codes will stop working.',
      'Remove',
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      await mfaService.disableTotp();
      await refresh();
      toast('Authenticator app removed');
    } catch (err) {
      errorToast(err);
    } finally {
      setBusy(false);
    }
  };

  const regenerateBackupCodes = async () => {
    const confirmed = await confirm(
      'Generate new backup codes?',
      'Your current backup codes will stop working straight away.',
      'Generate new codes',
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const response = await mfaService.regenerateBackupCodes();
      setRevealedCodes(response.backupCodes);
      await refresh();
    } catch (err) {
      errorToast(err);
    } finally {
      setBusy(false);
    }
  };

  const onTotpConfirmed = (codes: string[]) => {
    setSetupOpen(false);
    setRevealedCodes(codes);
    toast('Authenticator app turned on');
    refresh();
  };

  if (loading) {
    return (
      <Box sx={{ py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!status || status.isUsingSingleSignOn) {
    return (
      <Box sx={{ maxWidth: 640 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Two-factor authentication is managed by your single sign-on provider.
        </Typography>
      </Box>
    );
  }

  const statusChip = (on: boolean) => (
    <Chip
      size="small"
      label={on ? 'On' : 'Off'}
      color={on ? 'success' : 'default'}
      variant={on ? 'filled' : 'outlined'}
    />
  );

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Add a second step when you sign in with your password, so a password alone is not enough to reach your account.
      </Typography>

      {/* Authenticator app */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ py: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body1">Authenticator app</Typography>
            {statusChip(status.totpEnabled)}
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {status.totpEnabled
              ? `${status.backupCodesRemaining} backup code${status.backupCodesRemaining === 1 ? '' : 's'} remaining`
              : 'Use a code from an app like Google Authenticator or Authy.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {status.totpEnabled ? (
            <>
              <Button variant="outlined" disabled={busy} onClick={regenerateBackupCodes}>
                <Typography sx={{ px: 0.5 }}>New backup codes</Typography>
              </Button>
              <Button variant="outlined" disabled={busy} onClick={disableTotp}>
                <Typography sx={{ px: 0.5 }}>Remove</Typography>
              </Button>
            </>
          ) : (
            <Button variant="contained" disabled={busy} onClick={() => setSetupOpen(true)}>
              <Typography sx={{ px: 0.5 }}>Set up</Typography>
            </Button>
          )}
        </Stack>
      </Stack>

      <Divider />

      {/* Email codes */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ py: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body1">Email codes</Typography>
            {statusChip(status.emailEnabled)}
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            We email a one-time code to your address when you sign in.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {status.emailEnabled ? (
            <Button variant="outlined" disabled={busy} onClick={disableEmail}>
              <Typography sx={{ px: 0.5 }}>Turn off</Typography>
            </Button>
          ) : (
            <Button variant="contained" disabled={busy} onClick={enableEmail}>
              <Typography sx={{ px: 0.5 }}>Turn on</Typography>
            </Button>
          )}
        </Stack>
      </Stack>

      {setupOpen && (
        <TotpSetupDialog open={setupOpen} onClose={() => setSetupOpen(false)} onConfirmed={onTotpConfirmed} />
      )}
      {revealedCodes && (
        <BackupCodesDialog open={!!revealedCodes} codes={revealedCodes} onClose={() => setRevealedCodes(null)} />
      )}
    </Box>
  );
};
